import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router";
import { useDataProvider, useNotify } from "ra-core";
import {
  format,
  isToday,
  isTomorrow,
  isBefore,
  isAfter,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Search,
  CheckCircle2,
  Circle,
  Clock,
  User,
  Building2,
  Video,
  Edit2,
  Trash2,
  ExternalLink,
  FilterX,
  Plus,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Task, Deal, Company, Contact, Sale } from "../types";
import { getActivityTypeConfig, ACTIVITY_TYPES } from "./activityTypes";
import {
  AgendaDateQuickFilter,
  type QuickDateFilterKey,
  type DateRange,
} from "./AgendaDateQuickFilter";
import { AdvancedFilterDropdown } from "../deals/filters/AdvancedFilterDropdown";
import {
  AGENDA_SAVED_FILTERS_STORAGE_KEY,
  AGENDA_ACTIVE_FILTER_STORAGE_KEY,
  defaultAgendaFilters,
  useSavedFilters,
} from "../deals/filters/useSavedFilters";
import {
  evaluateTaskFilter,
  type TaskFilterEvaluationContext,
} from "../deals/filters/filterEvaluator";
import type { AdvancedFilter } from "../deals/filters/filterTypes";
import { cn } from "@/lib/utils";

interface AgendaListViewProps {
  tasks: Task[];
  salesList: Sale[];
  dealsMap: Record<string, Deal>;
  contactsMap: Record<string, Contact>;
  companiesMap: Record<string, Company>;
  selectedSalesId: string;
  onSelectSalesId?: (salesId: string) => void;
  onEditTask: (task: Task) => void;
  onRefresh: () => void;
  onCreateNew: () => void;
}

export function AgendaListView({
  tasks,
  salesList,
  dealsMap,
  contactsMap,
  companiesMap,
  selectedSalesId,
  onSelectSalesId,
  onEditTask,
  onRefresh,
  onCreateNew,
}: AgendaListViewProps) {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Filtro de Responsável / Membro do Time
  const [internalSalesId, setInternalSalesId] = useState<string>(
    selectedSalesId || "all",
  );
  const currentSalesId = onSelectSalesId ? selectedSalesId : internalSalesId;

  const handleSalesChange = (newSalesId: string) => {
    setInternalSalesId(newSalesId);
    onSelectSalesId?.(newSalesId);
  };

  // Filtro Rápido de Data (Tabs)
  const [quickDateKey, setQuickDateKey] =
    useState<QuickDateFilterKey>("all_todo");
  const [customRange, setCustomRange] = useState<DateRange | null>(null);

  // Filtros locais adicionais
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "done">(
    "pending",
  );

  // Filtro Avançado salvo ou em preview
  const [previewFilter, setPreviewFilter] = useState<AdvancedFilter | null>(
    null,
  );
  const { activeFilter } = useSavedFilters(
    AGENDA_SAVED_FILTERS_STORAGE_KEY,
    AGENDA_ACTIVE_FILTER_STORAGE_KEY,
    defaultAgendaFilters,
  );
  const currentAdvancedFilter = previewFilter || activeFilter;

  // Contexto para avaliação de filtros avançados (deals, contacts, companies)
  const evaluationContext: TaskFilterEvaluationContext = useMemo(() => {
    const salesUsersMap = Object.fromEntries(
      salesList.map((s) => [
        String(s.id),
        { id: s.id, name: `${s.first_name} ${s.last_name || ""}`.trim() },
      ]),
    );
    return {
      dealsMap,
      companiesMap,
      contactsMap,
      salesUsersMap,
    };
  }, [dealsMap, companiesMap, contactsMap, salesList]);

  // Alternar conclusão da atividade
  const handleToggleDone = useCallback(
    async (task: Task, e: React.MouseEvent) => {
      e.stopPropagation();
      const isDone = Boolean(task.done_date);
      const newDoneDate = isDone ? null : new Date().toISOString();
      try {
        await dataProvider.update("tasks", {
          id: task.id,
          data: { done_date: newDoneDate },
          previousData: task,
        });
        notify(
          isDone
            ? "Atividade marcada como pendente"
            : "Atividade concluída com sucesso! ✓",
          { type: isDone ? "info" : "success" },
        );
        onRefresh();
      } catch {
        notify("Erro ao atualizar status da atividade", { type: "error" });
      }
    },
    [dataProvider, notify, onRefresh],
  );

  // Excluir atividade
  const handleDeleteTask = useCallback(
    async (task: Task, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!confirm(`Deseja excluir a atividade "${task.text}"?`)) return;
      try {
        await dataProvider.delete("tasks", {
          id: task.id,
          previousData: task,
        });
        notify("Atividade excluída", { type: "info" });
        onRefresh();
      } catch {
        notify("Erro ao excluir atividade", { type: "error" });
      }
    },
    [dataProvider, notify, onRefresh],
  );

  // Filtragem completa das tarefas
  const filteredTasks = useMemo(() => {
    const now = new Date();
    const todayStart = startOfDay(now);
    const todayEnd = endOfDay(now);
    const tomorrowStart = startOfDay(addDays(now, 1));
    const tomorrowEnd = endOfDay(addDays(now, 1));
    const thisWeekStart = startOfWeek(now, { weekStartsOn: 0 });
    const thisWeekEnd = endOfWeek(now, { weekStartsOn: 0 });
    const nextWeekStart = startOfWeek(addWeeks(now, 1), { weekStartsOn: 0 });
    const nextWeekEnd = endOfWeek(addWeeks(now, 1), { weekStartsOn: 0 });

    return tasks.filter((task) => {
      // 1. Filtro por Vendedor / Equipe
      if (
        currentSalesId !== "all" &&
        String(task.sales_id) !== String(currentSalesId)
      ) {
        return false;
      }

      // 2. Filtro por Tipo
      if (typeFilter !== "all" && task.type !== typeFilter) {
        return false;
      }

      // 3. Filtro por Prioridade
      if (priorityFilter !== "all" && task.prioridade !== priorityFilter) {
        return false;
      }

      // 4. Filtro por Status
      const isDone = Boolean(task.done_date);
      if (statusFilter === "pending" && isDone) return false;
      if (statusFilter === "done" && !isDone) return false;

      // 5. Filtro Rápido de Data de Vencimento
      const due = task.due_date ? new Date(task.due_date) : null;

      if (quickDateKey === "all_todo") {
        if (isDone) return false;
      } else if (quickDateKey === "overdue") {
        if (!due || isDone || !isBefore(due, todayStart)) return false;
      } else if (quickDateKey === "today") {
        if (!due || isBefore(due, todayStart) || isAfter(due, todayEnd))
          return false;
      } else if (quickDateKey === "tomorrow") {
        if (!due || isBefore(due, tomorrowStart) || isAfter(due, tomorrowEnd))
          return false;
      } else if (quickDateKey === "this_week") {
        if (!due || isBefore(due, thisWeekStart) || isAfter(due, thisWeekEnd))
          return false;
      } else if (quickDateKey === "next_week") {
        if (!due || isBefore(due, nextWeekStart) || isAfter(due, nextWeekEnd))
          return false;
      } else if (quickDateKey === "custom" && customRange) {
        if (
          !due ||
          isBefore(due, customRange.start) ||
          isAfter(due, customRange.end)
        ) {
          return false;
        }
      }

      // 6. Busca por Texto Livre (Título, Negócio, Contato)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const textMatch = task.text?.toLowerCase().includes(q);
        const notesMatch = task.notes?.toLowerCase().includes(q);
        const deal = task.deal_id ? dealsMap[String(task.deal_id)] : null;
        const dealMatch = deal?.name?.toLowerCase().includes(q);
        const contact = task.contact_id
          ? contactsMap[String(task.contact_id)]
          : null;
        const contactMatch = contact
          ? `${contact.first_name} ${contact.last_name || ""}`
              .toLowerCase()
              .includes(q)
          : false;

        if (!textMatch && !notesMatch && !dealMatch && !contactMatch) {
          return false;
        }
      }

      // 7. Filtro Avançado Detalhado (Funil do Negócio, Etapa, etc.)
      if (currentAdvancedFilter) {
        const passesAdvanced = evaluateTaskFilter(
          task,
          currentAdvancedFilter,
          evaluationContext,
        );
        if (!passesAdvanced) return false;
      }

      return true;
    });
  }, [
    tasks,
    currentSalesId,
    typeFilter,
    priorityFilter,
    statusFilter,
    quickDateKey,
    customRange,
    searchQuery,
    dealsMap,
    contactsMap,
    currentAdvancedFilter,
    evaluationContext,
  ]);

  // Contadores para o sumário rápido
  const counts = useMemo(() => {
    const now = new Date();
    const todayStart = startOfDay(now);
    let pendingCount = 0;
    let overdueCount = 0;
    let doneCount = 0;

    const baseTasks =
      currentSalesId === "all"
        ? tasks
        : tasks.filter((t) => String(t.sales_id) === String(currentSalesId));

    baseTasks.forEach((t) => {
      const isDone = Boolean(t.done_date);
      if (isDone) {
        doneCount++;
      } else {
        pendingCount++;
        if (t.due_date && isBefore(new Date(t.due_date), todayStart)) {
          overdueCount++;
        }
      }
    });

    return { pendingCount, overdueCount, doneCount, total: baseTasks.length };
  }, [tasks, currentSalesId]);

  return (
    <div className="space-y-4">
      {/* 1. Barra de Filtragem Rápida de Data (Vencimento) */}
      <div className="p-3 bg-card/90 rounded-2xl border shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <AgendaDateQuickFilter
          activeKey={quickDateKey}
          onSelectKey={setQuickDateKey}
          customRange={customRange}
          onApplyCustomRange={setCustomRange}
        />

        {/* Resumo de atividades */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground self-end md:self-auto">
          <span>
            Mostrando <strong>{filteredTasks.length}</strong> de {tasks.length}{" "}
            atividades
          </span>
          {counts.overdueCount > 0 && (
            <Badge
              variant="destructive"
              className="h-5 px-1.5 text-[10px] font-bold"
            >
              {counts.overdueCount} vencida(s)
            </Badge>
          )}
        </div>
      </div>

      {/* 2. Barra de Busca e Filtros Complementares + Filtro Avançado */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 bg-card/60 p-3 rounded-2xl border border-border/70">
        <div className="flex items-center gap-2 flex-1 flex-wrap">
          {/* Campo de Busca Livre */}
          <div className="relative min-w-[200px] flex-1 max-w-sm">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar atividade, negócio, cliente..."
              className="pl-8.5 h-8.5 text-xs bg-background"
            />
          </div>

          {/* Filtro por Membro do Time */}
          <Select value={currentSalesId} onValueChange={handleSalesChange}>
            <SelectTrigger className="h-8.5 text-xs min-w-[170px] max-w-[240px] bg-background">
              <div className="flex items-center gap-1.5 truncate">
                <Users className="w-3.5 h-3.5 text-primary shrink-0" />
                <SelectValue placeholder="Membro do time" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs font-medium">
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-400 flex items-center justify-center text-[10px] font-bold">
                    👥
                  </span>
                  <span>Toda a Equipe (Todos)</span>
                </div>
              </SelectItem>
              {salesList.map((sale) => (
                <SelectItem
                  key={sale.id}
                  value={String(sale.id)}
                  className="text-xs"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                      {sale.first_name?.[0] || "U"}
                    </span>
                    <span className="font-semibold">
                      {sale.first_name} {sale.last_name || ""}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro por Tipo */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8.5 text-xs min-w-[130px] bg-background">
              <SelectValue placeholder="Tipo de atividade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                Todos os tipos
              </SelectItem>
              {Object.values(ACTIVITY_TYPES).map((item) => (
                <SelectItem
                  key={item.value}
                  value={item.value}
                  className="text-xs"
                >
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro por Prioridade */}
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="h-8.5 text-xs min-w-[130px] bg-background">
              <SelectValue placeholder="Prioridade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                Todas prioridades
              </SelectItem>
              <SelectItem value="alta" className="text-xs">
                Alta Prioridade
              </SelectItem>
              <SelectItem value="media" className="text-xs">
                Média Prioridade
              </SelectItem>
              <SelectItem value="baixa" className="text-xs">
                Baixa Prioridade
              </SelectItem>
            </SelectContent>
          </Select>

          {/* Filtro por Status (Pendentes / Concluídas / Todas) */}
          <Select
            value={statusFilter}
            onValueChange={(val: any) => setStatusFilter(val)}
          >
            <SelectTrigger className="h-8.5 text-xs min-w-[125px] bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending" className="text-xs">
                Pendentes
              </SelectItem>
              <SelectItem value="done" className="text-xs">
                Concluídas
              </SelectItem>
              <SelectItem value="all" className="text-xs">
                Todas (Status)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Lado Direito: Filtro Avançado (com funil, etc.) e Botão Nova Atividade */}
        <div className="flex items-center gap-2 self-end lg:self-auto flex-wrap">
          {/* Botão de Filtro Detalhado (Igual ao de Deals) */}
          <AdvancedFilterDropdown
            storageKey={AGENDA_SAVED_FILTERS_STORAGE_KEY}
            activeKey={AGENDA_ACTIVE_FILTER_STORAGE_KEY}
            fallbackFilters={defaultAgendaFilters}
            defaultEntity="activity"
            title="Filtros Detalhados"
            onPreviewFilter={setPreviewFilter}
          />

          <Button
            size="sm"
            onClick={onCreateNew}
            className="gap-1.5 h-8.5 text-xs font-semibold shadow-xs"
          >
            <Plus className="w-4 h-4" />
            Nova Atividade
          </Button>
        </div>
      </div>

      {/* 3. Tabela / Lista de Atividades */}
      {filteredTasks.length === 0 ? (
        <div className="p-12 text-center border rounded-2xl bg-card/40 flex flex-col items-center justify-center gap-3">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
            <FilterX className="w-6 h-6" />
          </div>
          <div>
            <h4 className="font-semibold text-sm">
              Nenhuma atividade encontrada
            </h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Tente alterar os filtros de data, tipo ou remover condições do
              filtro avançado.
            </p>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setQuickDateKey("all_todo");
                handleSalesChange("all");
                setTypeFilter("all");
                setPriorityFilter("all");
                setStatusFilter("all");
                setSearchQuery("");
                setPreviewFilter(null);
              }}
              className="text-xs h-8"
            >
              Limpar Filtros
            </Button>
            <Button
              size="sm"
              onClick={onCreateNew}
              className="text-xs h-8 gap-1"
            >
              <Plus className="w-3.5 h-3.5" />
              Criar Atividade
            </Button>
          </div>
        </div>
      ) : (
        <div className="border border-border/80 rounded-2xl bg-card overflow-hidden shadow-xs">
          <div className="overflow-x-auto scrollbar-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-border/70 bg-muted/40 text-muted-foreground font-semibold">
                  <th className="py-3 px-3.5 w-10 text-center">Status</th>
                  <th className="py-3 px-3 min-w-[240px]">Atividade</th>
                  <th className="py-3 px-3 min-w-[160px]">Vencimento</th>
                  <th className="py-3 px-3 min-w-[180px]">Negócio Vinculado</th>
                  <th className="py-3 px-3 min-w-[160px]">Contato / Empresa</th>
                  <th className="py-3 px-3 min-w-[130px]">Responsável</th>
                  <th className="py-3 px-3 w-28 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {filteredTasks.map((task) => {
                  const isDone = Boolean(task.done_date);
                  const typeConfig = getActivityTypeConfig(task.type);
                  const Icon = typeConfig.icon;

                  // Data de vencimento
                  const dueDate = task.due_date
                    ? new Date(task.due_date)
                    : null;
                  const isOverdue =
                    dueDate &&
                    !isDone &&
                    isBefore(dueDate, startOfDay(new Date()));
                  const isDueToday = dueDate && !isDone && isToday(dueDate);
                  const isDueTomorrow =
                    dueDate && !isDone && isTomorrow(dueDate);

                  // Relacionamentos
                  const deal = task.deal_id
                    ? dealsMap[String(task.deal_id)]
                    : null;
                  const contact = task.contact_id
                    ? contactsMap[String(task.contact_id)]
                    : null;
                  const company = task.company_id
                    ? companiesMap[String(task.company_id)]
                    : deal?.company_id
                      ? companiesMap[String(deal.company_id)]
                      : null;
                  const sale = salesList.find(
                    (s) => String(s.id) === String(task.sales_id),
                  );

                  return (
                    <tr
                      key={task.id}
                      onClick={() => onEditTask(task)}
                      className={cn(
                        "hover:bg-muted/40 transition-colors cursor-pointer group",
                        isDone && "opacity-60 bg-muted/10",
                      )}
                    >
                      {/* Checkbox de Conclusão */}
                      <td
                        className="py-3 px-3.5 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={(e) => handleToggleDone(task, e)}
                          className="text-muted-foreground hover:text-emerald-600 transition-colors cursor-pointer"
                          title={
                            isDone
                              ? "Marcar como pendente"
                              : "Concluir atividade"
                          }
                        >
                          {isDone ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-500 fill-emerald-100 dark:fill-emerald-950" />
                          ) : (
                            <Circle className="w-5 h-5 hover:text-emerald-600" />
                          )}
                        </button>
                      </td>

                      {/* Atividade (Ícone + Título + Tipo + Prioridade) */}
                      <td className="py-3 px-3">
                        <div className="flex items-start gap-2.5">
                          <div
                            className={cn(
                              "w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5",
                              typeConfig.colorClass,
                            )}
                          >
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span
                                className={cn(
                                  "font-semibold text-foreground text-xs leading-snug",
                                  isDone &&
                                    "line-through text-muted-foreground",
                                )}
                              >
                                {task.text}
                              </span>
                              {task.prioridade === "alta" && (
                                <Badge
                                  variant="destructive"
                                  className="h-4 px-1 text-[9px] font-bold uppercase"
                                >
                                  Alta
                                </Badge>
                              )}
                              {task.google_calendar_event_id && (
                                <span
                                  className="w-2 h-2 rounded-full bg-blue-500 shrink-0"
                                  title="Sincronizado com Google Agenda"
                                />
                              )}
                            </div>
                            {task.notes && (
                              <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                                {task.notes}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Vencimento */}
                      <td className="py-3 px-3">
                        {dueDate ? (
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5">
                              <Clock
                                className={cn(
                                  "w-3.5 h-3.5 shrink-0",
                                  isOverdue
                                    ? "text-rose-600 dark:text-rose-400"
                                    : isDueToday
                                      ? "text-amber-600 dark:text-amber-400"
                                      : "text-muted-foreground",
                                )}
                              />
                              <span
                                className={cn(
                                  "font-medium",
                                  isOverdue &&
                                    "text-rose-600 dark:text-rose-400 font-bold",
                                  isDueToday &&
                                    "text-amber-600 dark:text-amber-400 font-semibold",
                                )}
                              >
                                {isDueToday
                                  ? `Hoje às ${format(dueDate, "HH:mm")}`
                                  : isDueTomorrow
                                    ? `Amanhã às ${format(dueDate, "HH:mm")}`
                                    : format(dueDate, "dd/MM/yyyy HH:mm", {
                                        locale: ptBR,
                                      })}
                              </span>
                            </div>
                            {isOverdue && (
                              <span className="text-[10px] text-rose-600 dark:text-rose-400 font-semibold">
                                Vencida
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground italic text-[11px]">
                            Sem data definida
                          </span>
                        )}
                      </td>

                      {/* Negócio Vinculado */}
                      <td
                        className="py-3 px-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {deal ? (
                          <div className="flex flex-col gap-0.5">
                            <Link
                              to={`/deals/${deal.id}/show`}
                              className="font-semibold text-primary hover:underline flex items-center gap-1 truncate max-w-[200px]"
                              title={`Abrir negócio: ${deal.name}`}
                            >
                              <span className="truncate">{deal.name}</span>
                              <ExternalLink className="w-3 h-3 shrink-0 opacity-70" />
                            </Link>
                            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                              {deal.amount > 0 && (
                                <span className="font-medium text-foreground">
                                  {deal.amount.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </span>
                              )}
                              {deal.stage && (
                                <span className="px-1 py-0.2 rounded bg-muted uppercase tracking-wider text-[9px]">
                                  {deal.stage}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-[11px] italic">
                            Nenhum negócio
                          </span>
                        )}
                      </td>

                      {/* Contato / Organização */}
                      <td
                        className="py-3 px-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-col gap-0.5 max-w-[170px]">
                          {contact ? (
                            <Link
                              to={`/contacts/${contact.id}/show`}
                              className="font-medium hover:text-primary truncate flex items-center gap-1"
                            >
                              <User className="w-3 h-3 text-muted-foreground shrink-0" />
                              <span className="truncate">
                                {contact.first_name} {contact.last_name || ""}
                              </span>
                            </Link>
                          ) : null}
                          {company ? (
                            <Link
                              to={`/companies/${company.id}/show`}
                              className="text-[11px] text-muted-foreground hover:text-primary truncate flex items-center gap-1"
                            >
                              <Building2 className="w-3 h-3 shrink-0 opacity-70" />
                              <span className="truncate">{company.name}</span>
                            </Link>
                          ) : null}
                          {!contact && !company && (
                            <span className="text-muted-foreground text-[11px] italic">
                              Sem vínculo
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Responsável (Sales) */}
                      <td className="py-3 px-3">
                        {sale ? (
                          <div className="flex items-center gap-1.5">
                            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">
                              {sale.first_name?.[0] || "U"}
                            </span>
                            <span className="truncate font-medium">
                              {sale.first_name} {sale.last_name || ""}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-[11px] italic">
                            Não atribuído
                          </span>
                        )}
                      </td>

                      {/* Ações */}
                      <td
                        className="py-3 px-3 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                          {task.link_chamada && (
                            <a
                              href={task.link_chamada}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/50 rounded-md transition-colors"
                              title="Abrir link da reunião"
                            >
                              <Video className="w-3.5 h-3.5" />
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => onEditTask(task)}
                            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors cursor-pointer"
                            title="Editar atividade"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleDeleteTask(task, e)}
                            className="p-1.5 text-muted-foreground hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-md transition-colors cursor-pointer"
                            title="Excluir atividade"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
